import React, { useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/custom-theme-button';
import { DropdownItem as AkDropdownItem } from '@atlaskit/dropdown-menu';
import AkTooltip from '@atlaskit/tooltip/Tooltip';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { MOVE_PAGE_DIALOG_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { MoveBlogModalLoader } from '@confluence/move-blog-modal';
import { MovePageDialog } from '@confluence/page-tree/entry-points/MovePageDialog.tsx';

import { i18n } from './i18n.ts';
import EditorMovePageButtonQuery from './__generated__/EditorMovePageButtonQuery.graphql.ts';
import type {
	EditorMovePageButtonQuery$data,
	EditorMovePageButtonQuery$variables,
} from './__generated__/EditorMovePageButtonQuery.graphql.ts';
import type {
	EditorMovePageButtonComponentWithDialogsProps,
	EditorMovePageButtonComponentProps,
} from './types.ts';
const EditorMovePageButtonComponentWithDialogs: React.FC<
	EditorMovePageButtonComponentWithDialogsProps
> = ({
	spaceKey,
	contentId,
	appearance = 'dropdownItem',
	analyticsSource,
	dialogs,
	retrieveEditorTitleAndContent,
	isUnpublishedDraft,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error } = useQueryInterop<
		EditorMovePageButtonQuery$data,
		EditorMovePageButtonQuery$variables
	>(EditorMovePageButtonQuery, {
		variables: {
			contentId,
		},
	});
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (loading) return null;

	const content = data?.content?.nodes?.[0];
	const contentType = content?.type || '';
	const operations = data?.content?.nodes?.[0]?.operations || [];
	const canMoveContent =
		isUnpublishedDraft ||
		contentType === 'page' ||
		!!operations.find((op) => op?.targetType === contentType && op?.operation === 'delete');

	const openMoveModal = () => {
		if (contentType === 'page') {
			experienceTracker.start({
				name: MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
			});
			dialogs.showModal(MovePageDialog, {
				contentId,
				spaceKey,
				isUnpublishedDraft,
				isMovingDraftVersion: true,
				referrer: 'editor-move-page-button',
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'openEditorMovePageDialog',
					source: analyticsSource,
					objectId: contentId,
					objectType: 'page',
				},
			}).fire();
		} else if (contentType === 'blogpost') {
			dialogs.showModal(MoveBlogModalLoader, {
				currentSpaceKey: spaceKey,
				blogPostId: contentId,
				isEditMode: true,
				isUnpublishedDraft,
				retrieveEditorTitleAndContent,
				referrer: 'editor-move-page-button',
			});
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'openEditorMovePageDialog',
					source: analyticsSource,
					objectId: contentId,
					objectType: 'blog',
				},
			}).fire();
		}
	};

	let component;
	// if FF is on we should show move button for both blog and page types and for both publish and unpublish states
	if (canMoveContent || (isUnpublishedDraft && contentType === 'page')) {
		if (appearance === 'iconButton') {
			const icon = (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<span className="icon aui-icon aui-icon-small aui-iconfont-nav-children" />
			);

			component = (
				<AkTooltip tag="span" content={intl.formatMessage(i18n.movePageBtnDesc)}>
					<AkButton
						testId="move-page-icon-button"
						iconBefore={icon}
						appearance="subtle"
						onClick={openMoveModal}
					/>
				</AkTooltip>
			);
		} else if (appearance === 'dropdownItem') {
			component = (
				<AkDropdownItem onClick={openMoveModal} testId="move-page-dropdown-item">
					<FormattedMessage {...i18n.movePageBtnDesc} />
				</AkDropdownItem>
			);
		}
	}

	return (
		<div data-testid="move-page-button">
			{error && <ErrorDisplay error={error} />}
			{component}
		</div>
	);
};

export const EditorMovePageButton: React.FC<EditorMovePageButtonComponentProps> = (props) => {
	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => (
				<EditorMovePageButtonComponentWithDialogs {...props} dialogs={dialogs} />
			)}
		</Subscribe>
	);
};
